// ---------------------------------------------------------------------------
// AvatarBorderRing — o anel de uma moldura de avatar, em UM lugar só.
//
// Três superfícies mostram moldura e antes tinham três markups diferentes:
// o avatar (AvatarDisplay), "Meus Itens" (CosmeticPicker) e a vitrine da Loja
// (StoreShopTab) — que, pior, nem mostrava o anel: vendia a moldura de 1200
// moedas com um emoji. Aqui mora o único render, então o que o usuário vê
// antes de comprar é exatamente o que ele equipa depois.
//
// Estrutura de camadas (todas dentro de um stacking context isolado). A ORDEM
// no DOM é a ordem de pintura — todas são z-0 e empilham por posição:
//   wrapper  → anel base (`gradient`) + aresta usinada (`edge`, box-shadow
//              inset), padding = espessura
//   z-0      → varredura girante (conic), só em épico/lendário/founder
//   z-0      → faíscas orbitando, só em lendário
//   z-0      → luz FIXA (`specular`) — vem DEPOIS da varredura de propósito:
//              ela tem que ficar por cima do que gira, senão a textura passa
//              na frente do reflexo e o metal vira faixa colorida
//   z-0      → halo pulsante (box-shadow), em raro pra cima
//   z-10     → o conteúdo (avatar, emoji, inicial)
//
// `prefers-reduced-motion` não precisa de código aqui: o override universal de
// `styles/primitives.css` zera a duração de toda animação. A varredura congela
// num frame válido e o halo continua (opacidade base 0.75 na classe), então a
// moldura cara segue parecendo cara sem nada se mexendo.
// ---------------------------------------------------------------------------

import type { ReactNode } from "react";
import {
  avatarBorder,
  BORDER_SPARKS_BACKGROUND,
  BORDER_SPARKS_INSET,
  BORDER_SWEEP_CLASS,
  DEFAULT_BORDER_RING_PX,
  resolveBorderRingPx,
  type AvatarBorderItem,
} from "../../features/pro/constants/avatarBorders";

export interface AvatarBorderRingProps {
  /** Id da moldura (`users.activeAvatarBorder` / `store_items.id`). */
  borderId?: string | null;
  /**
   * Moldura CRUA, sem passar pelo catálogo — para o admin pré-visualizar uma
   * moldura ainda sendo desenhada no painel (Bloco A, Admin-Alavancas), que
   * por definição ainda não tem entrada em `avatarBorders.ts` nem em
   * `store_items`. Tem prioridade sobre `borderId` quando os dois vêm juntos.
   * Nenhum consumidor de produção usa isto — só a prévia do painel.
   */
  override?: AvatarBorderItem | null;
  /** Classes do wrapper — é aqui que vem o tamanho (`h-12 w-12`). */
  className?: string;
  /**
   * Halo somado ao da moldura. Usado pelo brilho de Founder: equipar uma
   * moldura comprada não pode apagar a identidade de quem é fundador.
   */
  extraGlow?: string;
  /**
   * Anel usado quando o id não resolve para nenhuma moldura do catálogo
   * (placeholder de card). Sem ele, um id desconhecido renderiza só o filho.
   */
  fallbackBackground?: string;
  /**
   * Lado do avatar em px CSS. É o que faz a espessura do anel ACOMPANHAR o
   * tamanho — sem ele, `ringPx` vale inteiro em qualquer medida e a moldura
   * fica grossa justamente onde o avatar é pequeno (liga, feed, busca).
   * Ausente = espessura desenhada, que é o comportamento histórico.
   */
  sizePx?: number;
  children: ReactNode;
}

export function AvatarBorderRing({
  borderId,
  override,
  className = "",
  extraGlow,
  fallbackBackground,
  sizePx,
  children,
}: AvatarBorderRingProps) {
  const border = override ?? avatarBorder(borderId);

  if (!border) {
    if (!fallbackBackground) return <>{children}</>;
    return (
      <div
        className={`relative isolate rounded-full ${className}`}
        style={{
          padding: resolveBorderRingPx(DEFAULT_BORDER_RING_PX, sizePx),
          background: fallbackBackground,
        }}
      >
        <div className="relative z-10 h-full w-full">{children}</div>
      </div>
    );
  }

  const sweepClass = border.effect ? BORDER_SWEEP_CLASS[border.effect] : "";
  const halo = [border.glow, extraGlow].filter(Boolean).join(", ");

  return (
    <div
      className={`relative isolate rounded-full ${className}`}
      style={{
        padding: resolveBorderRingPx(border.ringPx, sizePx),
        background: border.gradient,
        ...(border.edge ? { boxShadow: border.edge } : {}),
      }}
    >
      {border.sweep && sweepClass && (
        <span
          className={`absolute inset-0 z-0 rounded-full ${sweepClass}`}
          style={{ background: border.sweep }}
          aria-hidden
        />
      )}
      {border.effect === "aurora" && (
        <span
          className="lb-border-sparks absolute z-0 rounded-full"
          style={{ inset: BORDER_SPARKS_INSET, background: BORDER_SPARKS_BACKGROUND }}
          aria-hidden
        />
      )}
      {border.specular && (
        <span
          className="absolute inset-0 z-0 rounded-full"
          style={{ background: border.specular }}
          aria-hidden
        />
      )}
      {halo && (
        <span
          className="lb-border-pulse absolute inset-0 z-0 rounded-full"
          style={{ boxShadow: halo }}
          aria-hidden
        />
      )}
      <div className="relative z-10 h-full w-full">{children}</div>
    </div>
  );
}
